/**
 * Thin client for the control-plane API. Same-origin only: in dev Vite proxies /auth and /v1 to the
 * API, in prod the API serves this bundle. The session rides in the HttpOnly cookie, never in JS.
 */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly body: unknown,
  ) {
    super(message);
  }
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const init: RequestInit = { method, credentials: 'same-origin' };
  if (body !== undefined) {
    init.headers = { 'content-type': 'application/json' };
    init.body = JSON.stringify(body);
  }
  const res = await fetch(path, init);
  if (res.status === 204) return undefined as T;
  const text = await res.text();
  let data: unknown = null;
  try {
    data = text ? JSON.parse(text) : null;
  } catch {
    data = { error: 'bad_response', message: text.slice(0, 200) };
  }
  if (!res.ok) {
    const e = (data ?? {}) as { error?: string; message?: string; reason?: string };
    throw new ApiError(
      res.status,
      e.error ?? `http_${res.status}`,
      e.message ?? e.reason ?? e.error ?? `${res.status} ${res.statusText}`,
      data,
    );
  }
  return data as T;
}

export const get = <T>(path: string) => request<T>('GET', path);
export const post = <T>(path: string, body?: unknown) => request<T>('POST', path, body ?? {});
export const put = <T>(path: string, body: unknown) => request<T>('PUT', path, body);
export const del = <T = void>(path: string) => request<T>('DELETE', path);

// --- shapes mirrored from apps/api (kept small on purpose; the API is the source of truth) ---

export interface Passkey {
  id: string;
  name: string | null;
  deviceType: string;
  backedUp: boolean;
  transports: string[];
  createdAt: string;
  lastUsedAt: string | null;
}

export interface Recovery {
  id: string;
  status: 'pending' | 'cancelled' | 'completed';
  requestedAt: string;
  completesAt: string;
  cancelledAt: string | null;
  completedAt: string | null;
}

export interface Session {
  authenticated: true;
  userId: string;
  email: string | null;
  method: 'otp' | 'passkey' | 'dev';
  expiresAt: string;
  passkeys: Passkey[];
  /** D11: a pending account recovery pulled by email; a passkey holder can cancel it. */
  recovery: Recovery | null;
}

export type BillingMode = 'live' | 'test' | 'fake' | 'off';

export interface CreditPack {
  id: string;
  name: string;
  priceCents: number;
  creditsMicroUsd: number;
  description: string;
}

export interface Purchase {
  id: string;
  packId: string;
  priceCents: number;
  currency: string;
  creditsMicroUsd: number;
  status: 'pending' | 'paid' | 'failed' | 'expired';
  note: string | null;
  createdAt: string;
  paidAt: string | null;
}

export interface Balance {
  credits: number;
  debits: number;
  openHolds: number;
  available: number;
}

export interface LedgerEntry {
  id: string;
  kind: 'credit' | 'debit' | 'hold' | 'settle' | 'release';
  meter: string;
  amount: number;
  holdId: string | null;
  ref: Record<string, string>;
  at: string;
  seq: number;
}

export interface WorkspaceUsage {
  workspaceId: string | null;
  since?: string;
  samples: number;
  awakeSec: number;
  cpuSec: number;
  gbHours: number;
  chargedMicroUsd: { vm_cpu: number; vm_memory: number };
}

export interface Thread {
  id: string;
  userId: string;
  title: string | null;
  workflowId: string;
  createdAt: string;
  lastActivityAt: string;
}

export type SessionStatus = 'idle' | 'thinking' | 'running_tools' | 'awaiting_approval' | 'ended';
export interface PendingApproval {
  callId: string;
  connector: string;
  tool: string;
  args: Record<string, unknown>;
  since: string;
}
export interface SessionState {
  status: SessionStatus;
  step: number;
  queued: number;
  totalCostMicroUsd: number;
  lastError?: string;
  endedReason?: 'idle_timeout' | 'cancelled' | 'insufficient_credit';
  pending?: PendingApproval[];
}

/** AI SDK ModelMessage as persisted by the worker. */
export interface StoredMessage {
  id: string;
  threadId: string;
  role: 'system' | 'user' | 'assistant' | 'tool';
  content: { role: string; content: unknown };
  seq: number;
  at: string;
}

export type ToolRole = string;
export interface CapabilityMap {
  connectorId: string;
  roles: Partial<Record<ToolRole, string[]>>;
  orderPlacing: string[];
  unclassified: string[];
  source: 'default' | 'inferred' | 'user_confirmed';
}

export interface ToolSpec {
  name: string;
  title?: string;
  description?: string;
  inputSchema: unknown;
  annotations?: Record<string, unknown>;
  /** Server metadata; MCP Apps views live at `_meta.ui.resourceUri`. */
  _meta?: Record<string, unknown>;
}

export type WidgetKind = 'echarts' | 'table' | 'metric' | 'text';
export interface WidgetSource {
  connector: string;
  tool: string;
  args?: Record<string, unknown>;
  path?: string;
  refreshSec?: number;
}
export interface Widget {
  id: string;
  userId: string;
  kind: WidgetKind;
  title: string;
  spec: Record<string, unknown>;
  source?: WidgetSource;
  origin: string;
  createdAt: string;
  updatedAt: string;
}

export interface ResourceContent {
  uri: string;
  mimeType?: string;
  text?: string;
  blob?: string;
  _meta?: Record<string, unknown>;
}

export interface Connector {
  id: string;
  name: string;
  url: string;
  server: { name: string | null; version: string | null };
  status: 'active' | 'error' | 'disabled';
  lastError: string | null;
  toolCount: number;
  capabilityMap: CapabilityMap;
  createdAt: string;
  updatedAt: string;
}

export interface McpCallResult {
  isError: boolean;
  text: string;
  structured?: unknown;
  other: unknown[];
}

export type InvokeOutcome =
  | { status: 'ok'; result: McpCallResult }
  | { status: 'tool_error'; result: McpCallResult }
  | { status: 'denied'; reason: string }
  | { status: 'failed'; error: string };

export interface AuditEntry {
  id: string;
  at: string;
  tool: string;
  connector?: string | null;
  authorization: string | null;
  outcome: string;
  [k: string]: unknown;
}

export type JobStatus =
  | 'queued'
  | 'running'
  | 'succeeded'
  | 'failed'
  | 'killed'
  | 'timed_out'
  | 'stuck';
export const JOB_KINDS = [
  'shell',
  'python',
  'backtest',
  'watcher',
  'strategy_demo',
  'strategy_live',
] as const;
export type JobKind = (typeof JOB_KINDS)[number];
export interface Job {
  id: string;
  workspaceId: string;
  kind: JobKind;
  status: JobStatus;
  spec: {
    command: string;
    cwd?: string;
    timeoutSec: number;
    origin?: { source: string; threadId?: string };
  };
  startedAt: string | null;
  endedAt: string | null;
  exitCode: number | null;
  note: string | null;
  createdAt: string;
}
export interface JobLog {
  seq: number;
  jobId: string;
  stream: 'stdout' | 'stderr';
  chunk: string;
  at: string;
}
export const JOB_TERMINAL: ReadonlySet<JobStatus> = new Set([
  'succeeded',
  'failed',
  'killed',
  'timed_out',
  'stuck',
]);

export const api = {
  jobs: () => get<{ jobs: Job[] }>('/v1/jobs'),
  job: (id: string) => get<{ job: Job }>(`/v1/jobs/${id}`),
  jobLogs: (id: string, after = 0) =>
    get<{ entries: JobLog[]; next: number }>(`/v1/jobs/${id}/logs?after=${after}&limit=2000`),
  startJob: (body: { kind: JobKind; command: string; cwd?: string; timeoutSec: number }) =>
    post<{ id: string }>('/v1/jobs', body),
  killJob: (id: string) => post<{ job: Job }>(`/v1/jobs/${id}/kill`),

  session: () => get<Session>('/auth/session'),
  otpStart: (email: string) =>
    post<{ otpId: string; devCode?: string; mailError?: string }>('/auth/otp/start', { email }),
  otpVerify: (otpId: string, code: string) =>
    post<{
      token: string | null;
      userId: string;
      email: string;
      isNew: boolean;
      hasPasskey: boolean;
      recovery: Recovery | null;
    }>('/auth/otp/verify', { otpId, code }),
  recovery: (id: string) => get<{ recovery: Recovery }>(`/auth/recovery/${encodeURIComponent(id)}`),
  cancelRecoveryWithToken: (token: string) =>
    post<{ recovery: Recovery }>('/auth/recovery/cancel', { token }),
  cancelRecovery: (id: string) =>
    post<{ recovery: Recovery }>(`/auth/recovery/${encodeURIComponent(id)}/cancel`),
  logout: () => post<{ ok: true }>('/auth/logout'),
  passkeyRegisterOptions: () =>
    post<{ challengeId: string; options: unknown }>('/auth/passkey/register/options'),
  passkeyRegisterVerify: (challengeId: string, response: unknown, name?: string) =>
    post<{ id: string }>('/auth/passkey/register/verify', { challengeId, response, name }),
  passkeyLoginOptions: () =>
    post<{ challengeId: string; options: unknown }>('/auth/passkey/login/options'),
  passkeyLoginVerify: (challengeId: string, response: unknown) =>
    post<{ token: string; userId: string }>('/auth/passkey/login/verify', {
      challengeId,
      response,
    }),
  removePasskey: (id: string) => del(`/auth/passkeys/${encodeURIComponent(id)}`),

  credits: () => get<Balance>('/v1/me/credits'),
  billingPacks: () =>
    get<{ mode: BillingMode; packs: CreditPack[]; custom: { minCents: number; maxCents: number } }>(
      '/v1/billing/packs',
    ),
  checkout: (choice: { packId: string } | { amountCents: number }) =>
    post<{ purchaseId: string; url: string }>('/v1/billing/checkout', choice),
  purchases: () => get<{ purchases: Purchase[] }>('/v1/billing/purchases'),
  purchase: (id: string) => get<Purchase>(`/v1/billing/purchases/${encodeURIComponent(id)}`),
  /** DEV ONLY: the fake gateway's stand-in checkout page pays through this. */
  devPay: (purchaseId: string) =>
    post<{ outcome: string; balance: Balance }>('/v1/billing/dev/pay', { purchaseId }),
  ledger: (limit = 100) => get<{ entries: LedgerEntry[] }>(`/v1/me/ledger?limit=${limit}`),
  devTopup: (amountMicroUsd: number) =>
    post<{ balance: Balance }>('/v1/me/credits/dev-topup', { amountMicroUsd }),
  usage: (hours = 24) => get<WorkspaceUsage>(`/v1/workspace/usage?hours=${hours}`),
  audit: (limit = 100) => get<{ entries: AuditEntry[] }>(`/v1/me/audit?limit=${limit}`),

  threads: () => get<{ threads: Thread[] }>('/v1/threads'),
  thread: (id: string) =>
    get<{ thread: Thread; messages: StoredMessage[]; session: SessionState | null }>(
      `/v1/threads/${id}`,
    ),
  createThread: (text: string) =>
    post<{ id: string; workflowId: string; model: string }>('/v1/threads', { text }),
  sendMessage: (threadId: string, text: string) =>
    post<{ id: string }>(`/v1/threads/${threadId}/messages`, { text }),
  cancelThread: (threadId: string) => post<{ ok: true }>(`/v1/threads/${threadId}/cancel`),
  decide: (
    threadId: string,
    callId: string,
    decision: 'approve' | 'deny',
    args?: Record<string, unknown>,
  ) =>
    post<{ ok: true }>(`/v1/threads/${threadId}/approvals/${encodeURIComponent(callId)}`, {
      decision,
      ...(args ? { args } : {}),
    }),

  connectors: () => get<{ connectors: Connector[] }>('/v1/connectors'),
  connector: (name: string) =>
    get<{ connector: Connector; tools: ToolSpec[]; instructions: string | null }>(
      `/v1/connectors/${encodeURIComponent(name)}`,
    ),
  addConnector: (name: string, url: string) =>
    post<{ connector: Connector; tools: ToolSpec[] }>('/v1/connectors', { name, url }),
  refreshConnector: (name: string) =>
    post<{ connector: Connector; tools: ToolSpec[] }>(
      `/v1/connectors/${encodeURIComponent(name)}/refresh`,
    ),
  setMap: (name: string, map: CapabilityMap) =>
    put<{ connector: Connector }>(`/v1/connectors/${encodeURIComponent(name)}/map`, { map }),
  removeConnector: (name: string) => del(`/v1/connectors/${encodeURIComponent(name)}`),
  workspace: () =>
    get<{
      id: string;
      provider: string;
      status: unknown;
      retention: {
        idleDeleteMs: number;
        lastActivityAt: string | null;
        deleteAt: string | null;
        warnedAt: string | null;
      };
    }>('/v1/workspace'),
  widgets: () => get<{ widgets: Widget[] }>('/v1/widgets'),
  widget: (id: string) =>
    get<{ widget: Widget }>(`/v1/widgets/${encodeURIComponent(id)}`).then((r) => r.widget),
  createWidget: (body: {
    kind: WidgetKind;
    title: string;
    spec: Record<string, unknown>;
    source?: WidgetSource;
  }) => post<{ widget: Widget }>('/v1/widgets', body),
  removeWidget: (id: string) => del(`/v1/widgets/${encodeURIComponent(id)}`),

  config: () => get<{ sandboxOrigin: string | null }>('/v1/config'),
  resource: (name: string, uri: string) =>
    get<{ contents: ResourceContent[] }>(
      `/v1/connectors/${encodeURIComponent(name)}/resources?uri=${encodeURIComponent(uri)}`,
    ),

  // D10 workstation layouts (opaque dockview JSON per name)
  layout: (name: string) =>
    get<{ layout: Record<string, unknown> | null; updatedAt: string | null }>(
      `/v1/workstation/layouts/${encodeURIComponent(name)}`,
    ),
  saveLayout: (name: string, layout: Record<string, unknown>) =>
    put<{ ok: true; updatedAt: string }>(`/v1/workstation/layouts/${encodeURIComponent(name)}`, {
      layout,
    }),
  resetLayout: (name: string) => del(`/v1/workstation/layouts/${encodeURIComponent(name)}`),

  invoke: (name: string, tool: string, args: Record<string, unknown>, threadId?: string) =>
    post<InvokeOutcome>(`/v1/connectors/${encodeURIComponent(name)}/invoke`, {
      tool,
      args,
      ...(threadId ? { threadId } : {}),
    }),
};
